import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDriverProfile } from '../../hooks/useDriverProfile';
import { useMember } from '../../hooks/useMember';
import {
  useCheckNewAssignments,
  useNewAssignments,
} from '../../hooks/useNewAssignments';
import { dismissIn, dismissedIn, keepDismissedIn } from '../../lib/dismissals';
import { onPushReceived } from '../../lib/pushes';
import { AssignedCallSheet } from '../calls/AssignedCallSheet';
import type { CallRecord } from '../calls/callRecord';
import { useStartCall } from '../calls/useStartCall';

/**
 * "You've been assigned a call", over whatever screen the driver is on.
 *
 * The app has no live connection, so it asks the server for the driver's
 * assigned calls nobody has started: on opening, every 30 seconds while the
 * page is in view (the app's polling default), each time it comes back into
 * view, and at once when a web push for a new assignment arrives or a push
 * is tapped (MobileShell). The oldest the driver has not answered is asked
 * about, one at a time.
 *
 * Okay, tapping outside and Start Call answer it, remembered on this phone
 * for this driver. A call started or unassigned elsewhere leaves the list,
 * and with it the prompt; the answers are pruned to the list, so they do
 * not pile up. Only a member who drives is asked: an office member who does
 * not has no assigned calls.
 */
export function AssignedCallWatcher() {
  const navigate = useNavigate();
  const member = useMember();
  const { data: profile } = useDriverProfile();
  const check = useCheckNewAssignments();
  const { start, starting, error } = useStartCall();
  const list = profile ? `assigned-calls:${profile.id}` : null;
  const calls = useNewAssignments<CallRecord>({
    enabled: member.drives && !!list,
  });
  const [answered, setAnswered] = React.useState<Set<string>>(() =>
    list ? dismissedIn(list) : new Set()
  );

  React.useEffect(() => {
    if (list) setAnswered(dismissedIn(list));
  }, [list]);

  React.useEffect(
    () =>
      onPushReceived((data) => {
        if (data.type === 'new-assignment') check();
      }),
    [check]
  );

  const ids = calls.items.map((call) => call.id).join(',');
  const loaded = !calls.isLoading && !calls.isError;
  React.useEffect(() => {
    if (list && loaded) keepDismissedIn(list, ids ? ids.split(',') : []);
  }, [list, loaded, ids]);

  if (!member.drives || !list) return null;
  const call = calls.items.find((item) => !answered.has(item.id));
  if (!call) return null;

  const answer = () => {
    dismissIn(list, call.id);
    setAnswered((before) => new Set(before).add(call.id));
  };

  return (
    <AssignedCallSheet
      key={call.id}
      call={call}
      onOkay={answer}
      onStart={async () => {
        if (!(await start(call))) return;
        answer();
        navigate(`/calls/${call.id}`);
      }}
      starting={starting}
      error={error}
    />
  );
}
